import type { H3Event } from 'h3';

/**
 * List of routes that don't require authentication
 */
export const unauthenticatedRoutes: string[] = ['/login', '/reset-password'] as const;

const dynamicAllow = async (
  event: H3Event | undefined,
  ability: ReturnType<typeof defineAbility>
) => {
  if (event) {
    return await allows(event, ability);
  }
  return await allows(ability);
};

export const RouteAbilities = {
  '/contacts': async (event?: H3Event) => {
    const [canViewContacts, partnerFilterActive, supplierFilterActive] = await Promise.all([
      dynamicAllow(event, viewClientFilterAbility),
      dynamicAllow(event, viewPartnerFilterAbility),
      dynamicAllow(event, viewSupplierFilterAbility),
    ]);
    return canViewContacts || partnerFilterActive || supplierFilterActive;
  },
  '/contacts/clients': async (event?: H3Event) => {
    return await dynamicAllow(event, viewClientFilterAbility);
  },
  '/contacts/partners': async (event?: H3Event) => {
    return await dynamicAllow(event, viewPartnerFilterAbility);
  },
  '/contacts/suppliers': async (event?: H3Event) => {
    return await dynamicAllow(event, viewSupplierFilterAbility);
  },
  '/vessels': async (event?: H3Event) => {
    return await dynamicAllow(event, viewVesselAbility);
  },
  '/assignments': async (event?: H3Event) => {
    return await dynamicAllow(event, viewAssignmentAbility);
  },
  '/referrals': async (event?: H3Event) => {
    return await dynamicAllow(event, viewReferralAbility);
  },
  '/portCalls': async (event?: H3Event) => {
    return await dynamicAllow(event, viewPortCallAbility);
  },
  '/invoices': async (event?: H3Event) => {
    const [canViewTransaction, assignmentTransactionFilterActive] = await Promise.all([
      dynamicAllow(event, viewTransactionAbility),
      dynamicAllow(event, assignmentTransactionFilterAbility),
    ]);
    return canViewTransaction && !assignmentTransactionFilterActive;
  },
  '/officeExpenses/recurring': async (event?: H3Event) => {
    const [canViewRecurringTransaction, assignmentTransactionFilterActive] = await Promise.all([
      dynamicAllow(event, viewRecurringTransactionAbility),
      dynamicAllow(event, assignmentTransactionFilterAbility),
    ]);
    return canViewRecurringTransaction && !assignmentTransactionFilterActive;
  },
  '/officeExpenses': async (event?: H3Event) => {
    const [canViewOfficeExpense, assignmentTransactionFilterActive] = await Promise.all([
      dynamicAllow(event, viewOfficeExpenseAbility),
      dynamicAllow(event, assignmentTransactionFilterAbility),
    ]);
    return canViewOfficeExpense && !assignmentTransactionFilterActive;
  },
  '/calendar': async (event?: H3Event) => {
    const [
      canViewAssignment,
      canViewTransaction,
      canViewSupplierPayment,
      canViewPartnerPayment,
      canViewAssignmentPayment,
    ] = await Promise.all([
      dynamicAllow(event, viewAssignmentAbility),
      dynamicAllow(event, viewTransactionAbility),
      dynamicAllow(event, viewSupplierPaymentAbility),
      dynamicAllow(event, viewPartnerPaymentAbility),
      dynamicAllow(event, viewAssignmentPaymentAbility),
    ]);
    return (
      canViewAssignment ||
      canViewTransaction ||
      canViewSupplierPayment ||
      canViewPartnerPayment ||
      canViewAssignmentPayment
    );
  },
  '/analytics': async (event?: H3Event) => {
    const [
      canViewAssignment,
      canViewPortCall,
      canViewTransaction,
      canViewAssignmentPayment,
      canViewTransactionPayment,
      canViewSupplierPayment,
      canViewPartnerPayment,
    ] = await Promise.all([
      dynamicAllow(event, viewAssignmentAbility),
      dynamicAllow(event, viewPortCallAbility),
      dynamicAllow(event, viewTransactionAbility),
      dynamicAllow(event, viewAssignmentPaymentAbility),
      dynamicAllow(event, viewTransactionPaymentAbility),
      dynamicAllow(event, viewSupplierPaymentAbility),
      dynamicAllow(event, viewPartnerPaymentAbility),
    ]);
    return (
      canViewAssignment &&
      canViewPortCall &&
      canViewTransaction &&
      canViewAssignmentPayment &&
      canViewTransactionPayment &&
      canViewSupplierPayment &&
      canViewPartnerPayment
    );
  },
  '/settings/members': async (event?: H3Event) => {
    return await dynamicAllow(event, viewMemberAbility);
  },
  '/settings/templates': async (event?: H3Event) => {
    return await dynamicAllow(event, viewTemplateAbility);
  },
  '/settings/organizationPreferences': async (event?: H3Event) => {
    return await dynamicAllow(event, ownerAbility);
  },
  '/settings/platformConfig': async (event?: H3Event) => {
    return await dynamicAllow(event, ownerAbility);
  },
  '/settings/organizationConfig': async (event?: H3Event) => {
    return await dynamicAllow(event, viewOrganizationConfigAbility);
  },
  '/settings/roles': async (event?: H3Event) => {
    return await dynamicAllow(event, viewRoleAbility);
  },
};
